import { memo } from 'react';
import styles from './styles.module.scss';
import { Generator, GeneratorType, getGenerationType, UpgradeType } from '../../types/game-types';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import { ItemInfo } from '../ItemInfo';
import { PurchaseableTemplate } from '../PurchaseableTemplate';
import { humanNumber } from '@game-libs/exc-libs/src/helpers';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';
import { UnitStats } from '../UnitStats';
import { unitStats as stats } from '../../stats';
import { HelperTooltip } from '../components/HelperTooltip';
import tooltipStyles from '../components/TooltipContents/styles.module.scss';
import { getModifiedTicksToGenerate } from '../../store/helpers/get-modified-ticks-to-generate';

// TODO: Probably not needed because all 1st level generators have the mainIndividualGenerationStat property set
const baseGenerationUpgrade: Partial<Record<GeneratorType, UpgradeType | null>> = {
  warrior: UpgradeType.Barbarian,
  wizard: UpgradeType.Archimage,
  elf: UpgradeType.MultipleShot,
  bard: UpgradeType.TuningFork,
};

export const GameGenerator = memo(function GameGenerator({ generator }: { generator: Generator }) {
  const { t } = useTranslation('generators');
  const { t: tTooltip } = useTranslation('tooltip');
  const upgrades = useUpgradesStore((store) => store.upgrades);

  // Calculates base generatior for each generator based on multiplier mapping above: baseGenerationUpgrade
  let baseGenUpgrade = 0;
  if (baseGenerationUpgrade[generator.id]) {
    baseGenUpgrade = upgrades[generator.id][baseGenerationUpgrade[generator.id]!].multiplier;
  }
  const generatorGeneration =
    stats[generator.id].mainIndividualGenerationStat ?? generator.baseGeneration + baseGenUpgrade;
  // const generatorGeneration2 = stats[generator.id].mainIndividualGenerationStat;
  // console.log('generatorGeneration2', generatorGeneration2);
  const generationDescription = `generationDescription.${generator.id}`;
  const ticksToGenerate = getModifiedTicksToGenerate(upgrades[generator.id], generator, generator.id);

  return (
    <div
      className={`${styles.gameGenerator} ${styles['level-' + generator.level]} ${!generator.isUnlocked ? styles.locked : ''}`}
      data-testid={`purchase-generator-${generator.id}`}
    >
      <PurchaseableTemplate item={generator} key={generator.id}>
        <div className={`${styles.generatorsHeader} ${styles.bump}`} key={generator.owned}>
          <HelperTooltip
            delayDuration={300}
            sideOffset={10}
            content={
              <>
                <div className={tooltipStyles.tooltipDescription}>
                  {tTooltip('generatorOwned', { value: humanNumber(generator.owned) })}
                </div>
                <div className={tooltipStyles.tooltipDescription}>
                  {tTooltip('generatorPurchased', { value: humanNumber(generator.purchased) })}
                </div>
              </>
            }
            contentClassName={tooltipStyles.simpleTooltipContent}
          >
            <p className={styles.ownedCount} data-testid={`generator-owned-${generator.id}`}>
              {humanNumber(generator.owned)}
              <span>({generator.purchased})</span>
            </p>
          </HelperTooltip>
          <p>{t(generator.id, { count: generator.owned })}</p>
          {generator.owned > 0 && (
            <HelperTooltip content={<UnitStats generatorType={generator.id} />} sideOffset={15}>
              <div style={{ maxHeight: '12px' }}>
                <Icon hasBackground={false} iconId={'stats'} />
              </div>
            </HelperTooltip>
          )}
        </div>
        <ItemInfo item={generator}>
          <div>
            <Icon iconId={getGenerationType(generator.id)} />
            {t(`${generationDescription}`, {
              generatorGeneration: humanNumber(generatorGeneration, 3),
              rate: ticksToGenerate || '',
            })}
          </div>
        </ItemInfo>
      </PurchaseableTemplate>
    </div>
  );
});
